import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api.js';
import { useToast } from '../../context/ToastContext.jsx';
import ImageField from './ImageField.jsx';
import { money, sizeRank } from '../../config/brand.js';
import { ik } from '../../utils/imagekit.js';

const DEFAULT_SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

const sizeSummary = (p) =>
  (p.sizes || []).length
    ? [...p.sizes].sort((a, b) => sizeRank(a.size) - sizeRank(b.size)).map((s) => `${s.size}:${s.stock}`).join('  ')
    : '-';

export function ProductList() {
  const notify = useToast();
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState('');
  const [main, setMain] = useState('');
  const [error, setError] = useState('');

  const load = () => api.products().then(setProducts).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const list = useMemo(
    () => (products || [])
      .filter((p) => !main || p.mainCategory === main)
      .filter((p) => !q || `${p.name} ${p.sku} ${p.category}`.toLowerCase().includes(q.toLowerCase()))
      .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || ''))),
    [products, q, main]
  );

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try { await api.deleteProduct(p.id); notify('Product deleted', 'success'); load(); } catch (e) { notify(e.message, 'error'); }
  };

  if (error) return <p className="aerror">{error}</p>;
  return (
    <div>
      <div className="ahead">
        <h1 className="atitle">Products</h1>
        <Link to="/admin/products/new" className="abtn">Add product</Link>
      </div>
      <div className="atools">
        <input placeholder="Search by name, SKU or category" value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={main} onChange={(e) => setMain(e.target.value)}>
          <option value="">All categories</option>
          <option value="clothing">Clothing</option>
          <option value="handbags">Handbags</option>
        </select>
      </div>
      {!products ? <p className="amuted">Loading</p> : list.length === 0 ? (
        <p className="amuted">{products.length ? 'No products match your search.' : 'No products yet. Add your first product.'}</p>
      ) : (
        <div className="ascroll">
          <table className="atable">
            <thead>
              <tr><th></th><th>Name</th><th>Category</th><th className="num">Price</th><th>Stock by size</th><th className="num">Total</th><th>Tags</th><th></th></tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id}>
                  <td className="thumb">{p.image ? <img src={ik(p.image, 120)} alt="" /> : <span />}</td>
                  <td><Link to={`/admin/products/${p.id}`}>{p.name}</Link>{p.sku && <small>SKU {p.sku}</small>}</td>
                  <td>{p.mainCategory === 'clothing' ? 'Clothing' : 'Handbags'}<small>{p.category}</small></td>
                  <td className="num">{money(p.price)}{p.originalPrice ? <small><s>{money(p.originalPrice)}</s></small> : null}</td>
                  <td className="mono">{p.mainCategory === 'clothing' ? sizeSummary(p) : 'One size'}</td>
                  <td className="num">{p.stock}</td>
                  <td>{[p.newArrival && 'New', p.bestSeller && 'Best seller'].filter(Boolean).join(', ') || '-'}</td>
                  <td className="actions">
                    <Link to={`/admin/products/${p.id}`}>Edit</Link>
                    <button type="button" className="linkbtn" onClick={() => remove(p)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const BLANK = {
  name: '', mainCategory: 'clothing', category: '', price: '', originalPrice: '', sku: '', color: '', material: '',
  description: '', fitNotes: '', images: [], newArrival: false, bestSeller: false, stock: 0,
};

export function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useToast();
  const [form, setForm] = useState(BLANK);
  const [settings, setSettings] = useState(null);
  // sizeRows: [{ size, on, stock }] for every size option
  const [sizeRows, setSizeRows] = useState([]);
  const [bySize, setBySize] = useState(true);
  const [loading, setLoading] = useState(!!id);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.settings().then(setSettings).catch(() => setSettings({})); }, []);

  const options = (settings && settings.sizeOptions) || DEFAULT_SIZE_OPTIONS;

  // Build the size table from the options plus any custom sizes already on the product
  const makeRows = (existing = []) => {
    const all = [...options];
    existing.forEach((s) => { if (!all.includes(s.size)) all.push(s.size); });
    return all.sort((a, b) => sizeRank(a) - sizeRank(b)).map((size) => {
      const found = existing.find((s) => s.size === size);
      return { size, on: !!found, stock: found ? found.stock : 0 };
    });
  };

  useEffect(() => {
    if (!settings) return;
    if (!id) { setSizeRows(makeRows()); return; }
    api.product(id)
      .then((p) => {
        setForm({ ...BLANK, ...p, price: p.price ?? '', originalPrice: p.originalPrice ?? '', images: p.images || [] });
        setBySize(p.mainCategory === 'clothing' && (p.sizes || []).length > 0);
        setSizeRows(makeRows(p.sizes || []));
      })
      .catch((e) => { notify(e.message, 'error'); navigate('/admin/products'); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, settings]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cats = ((settings && settings.categories) || {})[form.mainCategory] || [];
  const isClothing = form.mainCategory === 'clothing';
  const total = sizeRows.filter((r) => r.on).reduce((s, r) => s + (parseInt(r.stock, 10) || 0), 0);

  const setRow = (size, patch) => setSizeRows((rows) => rows.map((r) => (r.size === size ? { ...r, ...patch } : r)));
  const addCustomSize = () => {
    const s = (window.prompt('Size label (for example 3XL or Free Size)') || '').trim().toUpperCase();
    if (!s) return;
    if (sizeRows.some((r) => r.size === s)) { notify('That size already exists', 'error'); return; }
    setSizeRows((rows) => [...rows, { size: s, on: true, stock: 0 }]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isClothing && bySize && !sizeRows.some((r) => r.on)) { notify('Select at least one size', 'error'); return; }
    setBusy(true);
    const payload = {
      ...form,
      price: Number(form.price),
      originalPrice: form.originalPrice === '' ? null : Number(form.originalPrice),
      sizes: isClothing && bySize ? sizeRows.filter((r) => r.on).map((r) => ({ size: r.size, stock: parseInt(r.stock, 10) || 0 })) : [],
      stock: parseInt(form.stock, 10) || 0,
    };
    try {
      if (id) await api.updateProduct(id, payload); else await api.addProduct(payload);
      notify(id ? 'Product updated' : 'Product added', 'success');
      navigate('/admin/products');
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !settings) return <p className="amuted">Loading</p>;

  return (
    <form onSubmit={submit} className="aform">
      <div className="ahead">
        <h1 className="atitle">{id ? 'Edit product' : 'Add product'}</h1>
        <Link to="/admin/products" className="abtn abtn--ghost">Back to products</Link>
      </div>

      <section className="acard">
        <h2>Basics</h2>
        <label>Name<input value={form.name} onChange={(e) => set('name', e.target.value)} required maxLength={140} /></label>
        <div className="arow">
          <label>Category
            <select value={form.mainCategory} onChange={(e) => { set('mainCategory', e.target.value); set('category', ''); }}>
              <option value="clothing">Clothing</option>
              <option value="handbags">Handbags</option>
            </select>
          </label>
          <label>Sub category
            <input list="subcats" value={form.category} onChange={(e) => set('category', e.target.value)} required placeholder="Choose or type" />
            <datalist id="subcats">{cats.map((c) => <option key={c} value={c} />)}</datalist>
          </label>
        </div>
        <div className="arow">
          <label>Price (Rs)<input type="number" min="1" step="1" value={form.price} onChange={(e) => set('price', e.target.value)} required /></label>
          <label>Original price (Rs, for sale items)<input type="number" min="0" step="1" value={form.originalPrice} onChange={(e) => set('originalPrice', e.target.value)} /></label>
          <label>SKU<input value={form.sku} onChange={(e) => set('sku', e.target.value)} maxLength={40} /></label>
        </div>
        <div className="arow">
          <label>Colour<input value={form.color} onChange={(e) => set('color', e.target.value)} maxLength={60} /></label>
          <label>Material<input value={form.material} onChange={(e) => set('material', e.target.value)} maxLength={120} /></label>
        </div>
        <label>Description<textarea rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} maxLength={2000} /></label>
        <div className="achecks">
          <label className="acheck"><input type="checkbox" checked={form.newArrival} onChange={(e) => set('newArrival', e.target.checked)} /> Show in New Arrivals</label>
          <label className="acheck"><input type="checkbox" checked={form.bestSeller} onChange={(e) => set('bestSeller', e.target.checked)} /> Show in Best Sellers</label>
        </div>
      </section>

      <section className="acard">
        <h2>Stock and sizes</h2>
        {isClothing ? (
          <>
            <label className="acheck">
              <input type="checkbox" checked={bySize} onChange={(e) => setBySize(e.target.checked)} /> Track stock by size (shown to customers as size buttons)
            </label>
            {bySize ? (
              <>
                <div className="ascroll">
                  <table className="atable atable--sizes">
                    <thead><tr><th>Size</th><th>Offer this size</th><th>Units in stock</th></tr></thead>
                    <tbody>
                      {sizeRows.map((r) => (
                        <tr key={r.size}>
                          <td><strong>{r.size}</strong></td>
                          <td><input type="checkbox" checked={r.on} onChange={(e) => setRow(r.size, { on: e.target.checked })} aria-label={`Offer size ${r.size}`} /></td>
                          <td><input type="number" min="0" step="1" value={r.stock} disabled={!r.on} onChange={(e) => setRow(r.size, { stock: e.target.value })} aria-label={`Stock for size ${r.size}`} /></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr><td colSpan={2}>Total units</td><td><strong>{total}</strong></td></tr></tfoot>
                  </table>
                </div>
                <div className="arow arow--tight">
                  <button type="button" className="abtn abtn--ghost" onClick={addCustomSize}>Add another size</button>
                </div>
              </>
            ) : (
              <label>Units in stock (no sizes, for example unstitched fabric)<input type="number" min="0" step="1" value={form.stock} onChange={(e) => set('stock', e.target.value)} /></label>
            )}
            <label>Fit and size notes (optional)<textarea rows={2} value={form.fitNotes} onChange={(e) => set('fitNotes', e.target.value)} maxLength={400} placeholder="For example: model is 5 ft 7 in and wears size S" /></label>
          </>
        ) : (
          <label>Units in stock<input type="number" min="0" step="1" value={form.stock} onChange={(e) => set('stock', e.target.value)} /></label>
        )}
      </section>

      <section className="acard">
        <h2>Images</h2>
        <p className="amuted">The first image is used on cards and as the main product image. Photos are resized automatically.</p>
        <ImageField value={form.images} onChange={(v) => set('images', v)} />
      </section>

      <div className="asticky">
        <button type="submit" className="abtn" disabled={busy}>{busy ? 'Saving' : id ? 'Save changes' : 'Add product'}</button>
        <Link to="/admin/products" className="abtn abtn--ghost">Cancel</Link>
      </div>
    </form>
  );
}
